/**
 * src/pages/Analytics.js
 * Analytics dashboard — configurable grid of report funnel tiles.
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import FunnelChart from '../components/FunnelChart';
import TimeSeriesChart from '../components/TimeSeriesChart';
import DurationChart from '../components/DurationChart';
import ActiveChart from '../components/ActiveChart';
import ComparisonChart from '../components/ComparisonChart';
import api from '../api/client';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function timeAgo(dateStr) {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d ago`;
  return new Date(dateStr).toLocaleDateString();
}

// ---------------------------------------------------------------------------
// Dashboard Tile — shows a funnel for one report
// ---------------------------------------------------------------------------

function DashboardTile({ section, index, totalSections, reports, isAdmin, onAssign, onRemove, onMove, refreshKey }) {
  const [tileData, setTileData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState('');

  const isTimeSeries = section.report_type === 'time_series';
  const isDuration = section.report_type === 'duration';
  const isActive = section.report_type === 'active';
  const isComparison = section.report_type === 'comparison';

  useEffect(() => {
    if (!section.report_id) return;
    const isRefresh = tileData !== null;
    if (isRefresh) { setRefreshing(true); } else { setLoading(true); }
    setError('');
    const endpoint = isComparison
      ? `/portal/analytics/reports/${section.report_id}/comparison`
      : isDuration
        ? `/portal/analytics/reports/${section.report_id}/duration`
        : isTimeSeries
          ? `/portal/analytics/reports/${section.report_id}/time-series`
          : isActive
            ? `/portal/analytics/reports/${section.report_id}/active?limit=0`
            : `/portal/analytics/reports/${section.report_id}/funnel`;
    api.get(endpoint)
      .then(res => setTileData(res.data))
      .catch(err => setError(err.response?.data?.message || 'Failed to load data'))
      .finally(() => { setLoading(false); setRefreshing(false); });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [section.report_id, section.report_type, refreshKey]);

  // Empty slot
  if (!section.report_id) {
    const availableReports = reports.filter(r => !r._assigned);
    return (
      <div className="analytics-dashboard-tile analytics-dashboard-tile-empty">
        <div style={{ textAlign: 'center', padding: '24px' }}>
          <div style={{ fontSize: '13px', color: 'var(--text-tertiary)', marginBottom: '12px' }}>
            Select a report for this section
          </div>
          {availableReports.length > 0 ? (
            <select className="form-select" style={{ maxWidth: '300px', margin: '0 auto' }}
              value="" onChange={e => {
                if (e.target.value === '__create__') {
                  // handled by parent via navigate
                  onAssign(index, '__create__');
                } else if (e.target.value) {
                  onAssign(index, e.target.value);
                }
              }}>
              <option value="">— Choose report —</option>
              {availableReports.map(r => (
                <option key={r.id} value={r.id}>{r.name}</option>
              ))}
              {isAdmin && <option value="__create__">+ Create new report</option>}
            </select>
          ) : (
            <div>
              <div style={{ fontSize: '13px', color: 'var(--text-tertiary)', marginBottom: '8px' }}>
                No unassigned reports available.
              </div>
              {isAdmin && (
                <Link to="/analytics/new" className="btn btn-primary btn-sm">Create Report</Link>
              )}
            </div>
          )}
          {isAdmin && (
            <div style={{ marginTop: '12px' }}>
              <button className="btn btn-ghost btn-sm" style={{ color: 'var(--danger)', fontSize: '12px' }}
                onClick={() => onRemove(index)}>Remove section</button>
            </div>
          )}
        </div>
      </div>
    );
  }

  // Assigned tile
  const hasTimeMetrics = !isTimeSeries && tileData?.steps?.some(s => s.time_from_previous);

  // Compute total events across all series for time-series summary
  const totalEvents = isTimeSeries && tileData?.series
    ? tileData.series.reduce((sum, s) => sum + s.data.reduce((acc, d) => acc + d.count, 0), 0)
    : 0;

  return (
    <div className="analytics-dashboard-tile">
      <div className="analytics-dashboard-tile-header">
        <div style={{ flex: 1, minWidth: 0 }}>
          <Link to={`/analytics/${section.report_id}`}
            style={{ fontSize: '14px', fontWeight: '600', color: 'var(--text-primary)', textDecoration: 'none' }}>
            {section.report_name || 'Unknown report'}
          </Link>
          {section.current_version_number && (
            <span className="badge badge-neutral" style={{ fontSize: '10px', marginLeft: '6px' }}>
              v{section.current_version_number}
            </span>
          )}
          {isTimeSeries && (
            <span className="badge badge-neutral" style={{ fontSize: '10px', marginLeft: '6px' }}>
              Time Series
            </span>
          )}
          {isDuration && (
            <span className="badge badge-neutral" style={{ fontSize: '10px', marginLeft: '6px' }}>
              Duration
            </span>
          )}
          {isActive && (
            <span className="badge badge-neutral" style={{ fontSize: '10px', marginLeft: '6px' }}>
              Active/WIP
            </span>
          )}
          {isComparison && (
            <span className="badge badge-neutral" style={{ fontSize: '10px', marginLeft: '6px' }}>
              Comparison
            </span>
          )}
        </div>
        {isAdmin && (
          <div style={{ display: 'flex', gap: '2px', alignItems: 'center', flexShrink: 0 }}>
            <button className="btn btn-ghost btn-sm" style={{ padding: '2px 6px' }}
              onClick={() => onMove(index, -1)} disabled={index === 0}
              title="Move up">↑</button>
            <button className="btn btn-ghost btn-sm" style={{ padding: '2px 6px' }}
              onClick={() => onMove(index, 1)} disabled={index === totalSections - 1}
              title="Move down">↓</button>
            <button className="btn btn-ghost btn-sm" style={{ padding: '2px 6px', color: 'var(--danger)' }}
              onClick={() => onRemove(index)} title="Remove section">×</button>
          </div>
        )}
      </div>

      <div style={{ padding: '0 16px 16px', opacity: refreshing ? 0.5 : 1, transition: 'opacity 0.2s' }}>
        {loading && !tileData && (
          <div style={{ color: 'var(--text-secondary)', fontSize: '13px', padding: '16px 0' }}>
            {isComparison ? 'Comparing scenarios…' : isDuration ? 'Computing durations…' : isTimeSeries ? 'Loading chart…' : isActive ? 'Finding active entities…' : 'Computing funnel…'}
          </div>
        )}
        {error && (
          <div className="alert alert-error" style={{ fontSize: '12px' }}>{error}</div>
        )}

        {/* Comparison tile */}
        {!loading && !error && isComparison && tileData?.scenario_a && (
          <>
            <ComparisonChart
              baseType={tileData.base_type}
              scenarioA={tileData.scenario_a}
              scenarioB={tileData.scenario_b}
              deltas={tileData.deltas}
              compact
            />
            <div style={{ marginTop: '8px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>
                {tileData.scenario_a.label} vs {tileData.scenario_b.label}
              </span>
              <Link to={`/analytics/${section.report_id}`}
                style={{ fontSize: '12px', color: 'var(--accent)', textDecoration: 'none' }}>
                View details →
              </Link>
            </div>
          </>
        )}

        {/* Time-series tile */}
        {!loading && !error && isTimeSeries && tileData?.series && (
          <>
            <TimeSeriesChart data={tileData.series} granularity={tileData.granularity} chartType={tileData.chart_type} />
            <div style={{ marginTop: '8px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>
                {totalEvents.toLocaleString()} events · {tileData.series.length} series · {tileData.granularity}
              </span>
              <Link to={`/analytics/${section.report_id}`}
                style={{ fontSize: '12px', color: 'var(--accent)', textDecoration: 'none' }}>
                View details →
              </Link>
            </div>
          </>
        )}

        {/* Duration tile */}
        {!loading && !error && isDuration && tileData?.transitions && (
          <>
            <DurationChart transitions={tileData.transitions} compact />
            <div style={{ marginTop: '8px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>
                {tileData.transitions.length} transition{tileData.transitions.length !== 1 ? 's' : ''}
                {tileData.summary?.overall_sla_compliance != null && (
                  <> · {tileData.summary.overall_sla_compliance}% SLA</>
                )}
              </span>
              <Link to={`/analytics/${section.report_id}`}
                style={{ fontSize: '12px', color: 'var(--accent)', textDecoration: 'none' }}>
                View details →
              </Link>
            </div>
          </>
        )}

        {/* Active/WIP tile */}
        {!loading && !error && isActive && tileData?.summary && (
          <>
            <ActiveChart summary={tileData.summary} compact />
            <div style={{ marginTop: '8px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>
                {tileData.summary.total_active} active{tileData.summary.total_stale > 0 ? ` · ${tileData.summary.total_stale} stale` : ''}
              </span>
              <Link to={`/analytics/${section.report_id}`}
                style={{ fontSize: '12px', color: 'var(--accent)', textDecoration: 'none' }}>
                View details →
              </Link>
            </div>
          </>
        )}

        {/* Funnel tile */}
        {!loading && !error && !isTimeSeries && !isDuration && !isActive && !isComparison && tileData?.steps && (
          <>
            <FunnelChart steps={tileData.steps} definition={{}} hasTimeMetrics={hasTimeMetrics} />
            {tileData.overall_completion && (
              <div style={{ marginTop: '8px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>
                  {tileData.overall_completion.rate}% completion · {tileData.overall_completion.count}/{tileData.overall_completion.total}
                </span>
                <Link to={`/analytics/${section.report_id}`}
                  style={{ fontSize: '12px', color: 'var(--accent)', textDecoration: 'none' }}>
                  View details →
                </Link>
              </div>
            )}
          </>
        )}

        {!loading && !error && !tileData?.steps && !tileData?.series && !tileData?.transitions && !tileData?.summary && !tileData?.scenario_a && (
          <div style={{ color: 'var(--text-tertiary)', fontSize: '13px', padding: '16px 0' }}>No data available.</div>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Manage Reports Panel
// ---------------------------------------------------------------------------

function ManageReportsPanel({ reports, isAdmin, onDelete }) {
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [processFilter, setProcessFilter] = useState('');

  async function handleDelete(id) {
    setDeleting(true);
    try {
      await onDelete(id);
      setConfirmDeleteId(null);
    } finally {
      setDeleting(false);
    }
  }

  // Build the filter dropdown from process tags actually present across all reports.
  // A report referencing N processes contributes N entries to the dropdown — and shows
  // up under each of them when filtered.
  const processOptions = (() => {
    const seen = new Map();
    for (const r of reports) {
      const ids = r.process_ids || [];
      const names = r.process_names || [];
      for (let i = 0; i < ids.length; i++) {
        if (!seen.has(ids[i])) seen.set(ids[i], names[i] || ids[i]);
      }
    }
    return [...seen.entries()]
      .map(([id, name]) => ({ value: id, label: name }))
      .sort((a, b) => a.label.localeCompare(b.label));
  })();

  const filteredReports = processFilter
    ? reports.filter(r => (r.process_ids || []).includes(processFilter))
    : reports;

  return (
    <div>
      {processOptions.length > 0 && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', paddingBottom: '12px', borderBottom: '1px solid var(--border)', marginBottom: '8px' }}>
          <label style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>Filter by process:</label>
          <select className="form-select" style={{ maxWidth: '260px', fontSize: '12px', padding: '4px 8px' }}
            value={processFilter} onChange={e => setProcessFilter(e.target.value)}>
            <option value="">All processes</option>
            {processOptions.map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
          <span style={{ fontSize: '12px', color: 'var(--text-tertiary)' }}>
            {filteredReports.length} of {reports.length}
          </span>
        </div>
      )}

      {filteredReports.length === 0 ? (
        <div style={{ color: 'var(--text-tertiary)', fontSize: '13px', padding: '8px 0' }}>
          {reports.length === 0 ? 'No reports created yet.' : 'No reports match this filter.'}
        </div>
      ) : (
        filteredReports.map(report => (
          <div key={report.id} style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            padding: '8px 0', borderBottom: '1px solid var(--border)'
          }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
                <Link to={`/analytics/${report.id}`}
                  style={{ fontSize: '13px', fontWeight: '600', color: 'var(--text-primary)', textDecoration: 'none' }}>
                  {report.name}
                </Link>
                <span className="badge badge-neutral" style={{ fontSize: '10px' }}>
                  {report.report_type === 'comparison' ? 'Comparison' : report.report_type === 'active' ? 'Active/WIP' : report.report_type === 'duration' ? 'Duration' : report.report_type === 'time_series' ? 'Time Series' : 'Funnel'}
                </span>
                {(report.process_ids || []).map((pid, i) => (
                  <span key={pid} className="badge badge-neutral" style={{ fontSize: '10px' }}
                    title={`Process: ${(report.process_names || [])[i] || pid}`}>
                    {(report.process_names || [])[i] || pid}
                  </span>
                ))}
                {report.current_version_number && (
                  <span className="badge badge-neutral" style={{ fontSize: '10px' }}>v{report.current_version_number}</span>
                )}
                {report._assigned && (
                  <span style={{ fontSize: '10px', color: 'var(--accent)', fontWeight: '500' }}>on dashboard</span>
                )}
              </div>
              {report.description && (
                <div style={{ fontSize: '12px', color: 'var(--text-secondary)', marginTop: '1px' }}>{report.description}</div>
              )}
              <div style={{ fontSize: '11px', color: 'var(--text-tertiary)', marginTop: '2px' }}>
                Updated {timeAgo(report.updated_at)}
              </div>
            </div>
            <div style={{ display: 'flex', gap: '6px', alignItems: 'center', flexShrink: 0 }}>
              <Link to={`/analytics/${report.id}`} className="btn btn-ghost btn-sm">View</Link>
              {isAdmin && (
                <>
                  <Link to={`/analytics/edit/${report.id}`} className="btn btn-ghost btn-sm">Edit</Link>
                  {confirmDeleteId === report.id ? (
                    <div style={{ display: 'flex', gap: '4px' }}>
                      <button className="btn btn-danger btn-sm" onClick={() => handleDelete(report.id)} disabled={deleting}>
                        {deleting ? '…' : 'Confirm'}
                      </button>
                      <button className="btn btn-ghost btn-sm" onClick={() => setConfirmDeleteId(null)}>Cancel</button>
                    </div>
                  ) : (
                    <button className="btn btn-ghost btn-sm" style={{ color: 'var(--danger)' }}
                      onClick={() => setConfirmDeleteId(report.id)}>Delete</button>
                  )}
                </>
              )}
            </div>
          </div>
        ))
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main Analytics Dashboard
// ---------------------------------------------------------------------------

export default function Analytics() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isAdmin = user?.role === 'admin';

  const [sections, setSections] = useState([]);
  const [reports, setReports] = useState([]);
  const [initialLoading, setInitialLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState('');
  const [showManage, setShowManage] = useState(false);
  const [saving, setSaving] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const hasFetched = useRef(false);

  const fetchData = useCallback(async () => {
    setError('');
    if (hasFetched.current) { setRefreshing(true); } else { setInitialLoading(true); }
    try {
      const [dashRes, reportsRes] = await Promise.all([
        api.get('/portal/analytics/dashboard'),
        api.get('/portal/analytics/reports')
      ]);
      setSections(dashRes.data.sections || []);
      setReports(reportsRes.data || []);
      hasFetched.current = true;
    } catch (err) {
      if (!err.response) {
        setError('Unable to reach the server. Check your connection and try again.');
      } else {
        setError(err.response?.data?.message || 'Failed to load analytics data.');
      }
    } finally {
      setInitialLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Mark reports that are assigned to a dashboard section
  const assignedIds = new Set(sections.map(s => s.report_id).filter(Boolean));
  const enrichedReports = reports.map(r => ({ ...r, _assigned: assignedIds.has(r.id) }));

  async function saveDashboard(newSections) {
    setSaving(true);
    const oldSections = sections;
    setSections(newSections); // optimistic
    try {
      const res = await api.put('/portal/analytics/dashboard', {
        sections: newSections.map(s => ({ report_id: s.report_id || null }))
      });
      setSections(res.data.sections || newSections);
    } catch (err) {
      setSections(oldSections); // revert
      setError(err.response?.data?.message || 'Failed to save dashboard layout.');
    } finally {
      setSaving(false);
    }
  }

  function handleAddSection() {
    saveDashboard([...sections, { report_id: null, report_name: null, report_description: null }]);
  }

  function handleRemoveSection(index) {
    saveDashboard(sections.filter((_, i) => i !== index));
  }

  function handleMoveSection(index, direction) {
    const newIndex = index + direction;
    if (newIndex < 0 || newIndex >= sections.length) return;
    const newSections = [...sections];
    [newSections[index], newSections[newIndex]] = [newSections[newIndex], newSections[index]];
    saveDashboard(newSections);
  }

  function handleAssignReport(sectionIndex, reportId) {
    if (reportId === '__create__') {
      navigate('/analytics/new');
      return;
    }
    const report = reports.find(r => r.id === reportId);
    const newSections = [...sections];
    newSections[sectionIndex] = {
      report_id: reportId,
      report_name: report?.name || null,
      report_description: report?.description || null,
      current_version_number: report?.current_version_number || null,
      report_type: report?.report_type || null
    };
    saveDashboard(newSections);
  }

  async function handleDeleteReport(id) {
    try {
      await api.delete(`/portal/analytics/reports/${id}`);
      await fetchData(); // refresh both reports and dashboard (backend cleans up layout)
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to delete report.');
    }
  }

  // Initial loading
  if (initialLoading) return (
    <div className="page">
      <div style={{ color: 'var(--text-secondary)', fontSize: '13px' }}>Loading…</div>
    </div>
  );

  // Error with no data
  if (error && reports.length === 0 && sections.length === 0) return (
    <div className="page">
      <div className="page-header">
        <h1 className="page-title">Analytics</h1>
      </div>
      <div className="alert alert-error">
        {error}
        <button className="alert-retry" onClick={fetchData}>Try again</button>
      </div>
    </div>
  );

  // No reports exist at all
  if (reports.length === 0) return (
    <div className="page">
      <div className="page-header">
        <h1 className="page-title">Analytics</h1>
        <p className="page-subtitle">Create reports to analyze how entities flow through your processes.</p>
      </div>
      <div className="card">
        <div className="empty-state">
          <div className="empty-state-icon" style={{ fontSize: '32px' }}>&#x1F4CA;</div>
          <div className="empty-state-title">No reports yet</div>
          <div className="empty-state-text">
            Create a report to define how process steps connect and visualize entity flow through your funnel.
          </div>
          {isAdmin && (
            <Link to="/analytics/new" className="btn btn-primary" style={{ marginTop: '12px' }}>
              Create your first report
            </Link>
          )}
        </div>
      </div>
    </div>
  );

  return (
    <div className="page">
      {/* Header */}
      <div className="page-header" style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
        <div>
          <h1 className="page-title">Analytics</h1>
          <p className="page-subtitle">Monitor your process analytics at a glance.</p>
        </div>
        <div style={{ display: 'flex', gap: '8px' }}>
          <button className="btn btn-secondary" onClick={() => { fetchData(); setRefreshKey(k => k + 1); }}
            disabled={refreshing}>
            Refresh
          </button>
          {isAdmin && (
            <button className="btn btn-secondary" onClick={handleAddSection} disabled={saving || sections.length >= 12}>
              + Add Section
            </button>
          )}
        </div>
      </div>

      {error && <div className="alert alert-error" style={{ marginBottom: '16px' }}>{error}</div>}

      {/* Dashboard Grid */}
      {sections.length > 0 ? (
        <div className="analytics-dashboard-grid">
          {sections.map((section, i) => (
            <DashboardTile
              key={`${section.report_id || 'empty'}-${i}`}
              section={section}
              index={i}
              totalSections={sections.length}
              reports={enrichedReports}
              isAdmin={isAdmin}
              onAssign={handleAssignReport}
              onRemove={handleRemoveSection}
              onMove={handleMoveSection}
              refreshKey={refreshKey}
            />
          ))}
        </div>
      ) : (
        <div className="card" style={{ marginBottom: '16px' }}>
          <div className="card-body" style={{ textAlign: 'center', padding: '32px' }}>
            <div style={{ fontSize: '13px', color: 'var(--text-secondary)', marginBottom: '12px' }}>
              {isAdmin
                ? 'Add sections to pin report funnels to your dashboard.'
                : 'No dashboard sections configured yet.'}
            </div>
            {isAdmin && (
              <button className="btn btn-primary" onClick={handleAddSection}>
                + Add Section
              </button>
            )}
          </div>
        </div>
      )}

      {/* Manage Reports */}
      <div className="card" style={{ marginTop: '24px' }}>
        <div className="card-header">
          <span className="card-title">Reports</span>
        </div>
        <div className="card-body" style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
          <button className="btn btn-secondary" onClick={() => setShowManage(v => !v)}>
            {showManage ? 'Hide' : 'Manage'} Reports ({reports.length})
          </button>
          {isAdmin && (
            <Link to="/analytics/new" className="btn btn-primary">
              Create Report
            </Link>
          )}
        </div>
      </div>

      {showManage && (
        <div className="card" style={{ marginTop: '8px' }}>
          <div className="card-body">
            <ManageReportsPanel
              reports={enrichedReports}
              isAdmin={isAdmin}
              onDelete={handleDeleteReport}
            />
          </div>
        </div>
      )}
    </div>
  );
}
